<template>
  <Teleport to="body">
    <div v-if="isOpen" class="fixed inset-0 z-50">
      <div class="fixed inset-0 bg-slate-900/40 dark:bg-black/60" @click="requestClose" />

      <section
        ref="panelRef"
        class="fixed inset-y-0 right-0 flex w-full max-w-none flex-col overflow-y-auto bg-slate-50 shadow-xl focus:outline-none dark:bg-slate-900 dark:shadow-black/60 dark:ring-1 dark:ring-slate-700 lg:w-1/2"
        role="dialog"
        aria-modal="true"
        aria-label="Task detail"
        tabindex="-1"
        @keydown="handleKeydown"
      >
        <div v-if="error" class="mx-5 mt-5 bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950/40 dark:text-red-300">
          {{ error }}
        </div>

        <TaskDetail
          v-if="currentTaskId"
          ref="taskDetailRef"
          :store="store"
          :task-id="currentTaskId"
          :can-edit="canEdit"
          :lock-holder="lockHolder"
          tabbed-parts
          @deleted="handleDeleted"
          @moved="handleMoved"
          @request-close="requestClose"
        />
      </section>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import TaskDetail from './TaskDetail.vue'

const props = defineProps({
  store: {
    type: Object,
    required: true,
  },
  projects: {
    type: Array,
    default: () => [],
  },
  initialTaskId: {
    type: [String, Number],
    default: null,
  },
  currentUserLabel: {
    type: String,
    default: '',
  },
  isWorkspaceOwner: {
    type: Boolean,
    default: false,
  },
})

const panelRef = ref(null)
const taskDetailRef = ref(null)
const error = ref('')
const lockedInfo = ref(null)
const initialOpenPending = ref(props.initialTaskId !== null && props.initialTaskId !== undefined)
const openingFromPopstate = ref(false)
const hasDrawerHistoryEntry = ref(false)
const isClosing = ref(false)
const closeAfterErrorTimer = ref(null)
const previousBodyOverflow = ref('')
const bodyLocked = ref(false)
let previousFocus = null
let pageExitReleaseSent = false
let revertingTaskSwitch = false
let deletedTaskPendingClose = null
let workspaceUrl = currentLocationUrl()

const isOpen = computed(() => props.store.openTaskId.value !== null && props.store.openTaskId.value !== undefined)
const currentTaskId = computed(() => normalizeId(props.store.openTaskId.value))
const task = computed(() => {
  const detail = props.store.activeTaskDetail.value
  return normalizeId(detail?.id) === currentTaskId.value ? detail : null
})
const canEdit = computed(() => Boolean(task.value && props.store.heldToken(task.value.id)))
const lockHolder = computed(() => {
  const label = lockedInfo.value?.locked_by_label || task.value?.locked_by_label
  const via = lockedInfo.value?.locked_via || task.value?.locked_via
  if (!label && !via) return null
  return `${label || 'another user'}${via ? ` via ${via}` : ''}`
})

watch(
  () => props.projects,
  (projects) => props.store.setProjects?.(projects),
  { immediate: true }
)

watch(
  () => props.store.openTaskId.value,
  async (nextId, previousId) => {
    if (revertingTaskSwitch) {
      revertingTaskSwitch = false
      return
    }

    try {
      if (nextId !== null && nextId !== undefined) {
        if (previousId !== null && previousId !== undefined && normalizeId(previousId) !== normalizeId(nextId)) {
          try {
            if (shouldSkipCleanupForDeletedTask(previousId)) {
              clearLockState()
            } else {
              await flushAndUnlock(previousId)
            }
          } catch (requestError) {
            keepTaskOpenAfterSaveFailure(previousId, requestError)
            return
          }
        }

        if (previousId === null || previousId === undefined) {
          previousFocus = document.activeElement
        }

        clearLockState()
        error.value = ''
        lockBodyScroll()
        await nextTick()
        focusPanel()
        syncHistoryOnOpen(nextId)
        const lockAcquired = await acquireLockForTask(nextId)
        if (normalizeId(currentTaskId.value) !== normalizeId(nextId)) {
          if (lockAcquired) await props.store.unlockTask(nextId)
          return
        }
        await loadDetail(nextId)
        return
      }

      if (previousId !== null && previousId !== undefined) {
        try {
          if (shouldSkipCleanupForDeletedTask(previousId)) {
            clearLockState()
          } else {
            await flushAndUnlock(previousId)
          }
        } catch (requestError) {
          keepTaskOpenAfterSaveFailure(previousId, requestError)
          return
        }
      }

      clearLockState()
      error.value = ''
      openingFromPopstate.value = false
      restoreBodyScroll()
      restoreFocus()
    } finally {
      isClosing.value = false
    }
  }
)

onMounted(() => {
  window.addEventListener('popstate', handlePopstate)
  window.addEventListener('beforeunload', handleBeforeUnload)
  window.addEventListener('pagehide', releaseLockOnPageExit)
})

onBeforeUnmount(() => {
  releaseCurrentLock().catch(() => {})
  window.removeEventListener('popstate', handlePopstate)
  window.removeEventListener('beforeunload', handleBeforeUnload)
  window.removeEventListener('pagehide', releaseLockOnPageExit)
  clearCloseAfterErrorTimer()
  restoreBodyScroll()
})

async function acquireLockForTask(id) {
  const taskId = normalizeId(id)
  if (!taskId) return false

  if (props.store.heldToken(taskId)) {
    clearLockState()
    return true
  }

  try {
    await props.store.lockTask(taskId, {
      locked_by_label: props.currentUserLabel,
      locked_via: 'web',
    })

    if (normalizeId(currentTaskId.value) === taskId) {
      clearLockState()
    }
    return true
  } catch (requestError) {
    if (normalizeId(currentTaskId.value) === taskId) {
      applyLockError(requestError)
    }
    return false
  }
}

async function loadDetail(id) {
  clearCloseAfterErrorTimer()
  error.value = ''

  try {
    await props.store.loadTaskDetail(id)
  } catch (requestError) {
    error.value = requestError?.message || 'Task could not be loaded'
    closeAfterErrorTimer.value = window.setTimeout(() => {
      if (normalizeId(currentTaskId.value) === normalizeId(id)) {
        requestClose()
      }
    }, 1600)
  }
}

function syncHistoryOnOpen(id) {
  const taskId = normalizeId(id)

  if (openingFromPopstate.value) {
    openingFromPopstate.value = false
    return
  }

  if (initialOpenPending.value && taskId === normalizeId(props.initialTaskId)) {
    initialOpenPending.value = false
    hasDrawerHistoryEntry.value = false
    rememberWorkspaceUrl()
    history.replaceState({ ...(history.state || {}), tasksView: true, openTaskId: taskId, initialTaskEntry: true }, '', window.location.href)
    return
  }

  rememberWorkspaceUrl()
  history.pushState({ tasksView: true, openTaskId: taskId }, '', `/tasks/${encodeURIComponent(taskId)}`)
  hasDrawerHistoryEntry.value = true
}

function handlePopstate(event) {
  const stateTaskId = event.state?.tasksView ? event.state.openTaskId : taskIdFromPath()

  openingFromPopstate.value = true

  if (stateTaskId) {
    hasDrawerHistoryEntry.value = !event.state?.initialTaskEntry
    props.store.openTask(stateTaskId)
  } else {
    hasDrawerHistoryEntry.value = false
    props.store.closeTask()
  }
}

function taskIdFromPath() {
  const match = window.location.pathname.match(/^\/tasks\/([^/]+)$/)
  return match ? decodeURIComponent(match[1]) : null
}

async function requestClose() {
  if (isClosing.value) return

  isClosing.value = true
  clearCloseAfterErrorTimer()

  try {
    await flushAndUnlock(currentTaskId.value)

    if (hasDrawerHistoryEntry.value) {
      history.back()
      return
    }

    closeTaskWithoutDrawerHistory()
  } catch (requestError) {
    error.value = saveFailureMessage(requestError)
    isClosing.value = false
  }
}

function handleDeleted(deletedTaskId) {
  deletedTaskPendingClose = normalizeId(deletedTaskId)
  isClosing.value = true
  error.value = ''
  clearCloseAfterErrorTimer()

  if (hasDrawerHistoryEntry.value) {
    history.back()
    return
  }

  closeTaskWithoutDrawerHistory()
}

function handleMoved(movedTask) {
  const workspaceSlug = movedTask?.workspace?.slug
  if (!workspaceSlug) {
    requestClose()
    return
  }

  window.location.href = `/workspaces/${encodeURIComponent(workspaceSlug)}`
}

function closeTaskWithoutDrawerHistory() {
  props.store.closeTask()
  replaceHistoryWithWorkspace()
  isClosing.value = false
}

function replaceHistoryWithWorkspace() {
  const state = { ...(history.state || {}) }
  delete state.tasksView
  delete state.openTaskId
  delete state.initialTaskEntry
  history.replaceState(state, '', workspaceUrl)
  hasDrawerHistoryEntry.value = false
}

async function flushAndUnlock(id) {
  const taskId = normalizeId(id)
  if (!taskId) return

  await taskDetailRef.value?.flushPendingSave?.(taskId)
  await props.store.unlockTask(taskId)
}

async function releaseCurrentLock() {
  await flushAndUnlock(currentTaskId.value)
}

function releaseLockOnPageExit() {
  const taskId = normalizeId(currentTaskId.value)
  const token = taskId ? props.store.heldToken(taskId) : null
  if (!taskId || !token || pageExitReleaseSent) return

  pageExitReleaseSent = true
  fetch(props.store.lockEndpointUrl(taskId), {
    method: 'DELETE',
    headers: {
      'Accept': 'application/json',
      'X-CSRF-Token': csrfToken(),
      'X-Lock-Token': token,
    },
    credentials: 'same-origin',
    keepalive: true,
  }).catch(() => {})
}

function handleBeforeUnload(event) {
  if (!taskDetailRef.value?.hasUnsavedChanges?.()) return

  event.preventDefault()
  event.returnValue = ''
}

function keepTaskOpenAfterSaveFailure(id, requestError) {
  const wasOpeningFromPopstate = openingFromPopstate.value
  error.value = saveFailureMessage(requestError)
  openingFromPopstate.value = false
  isClosing.value = false

  const taskId = normalizeId(id)
  if (!taskId || normalizeId(props.store.openTaskId.value) === taskId) return

  if (wasOpeningFromPopstate) {
    history.replaceState({ ...(history.state || {}), tasksView: true, openTaskId: taskId }, '', `/tasks/${encodeURIComponent(taskId)}`)
  }

  revertingTaskSwitch = true
  props.store.openTask(taskId)
}

function saveFailureMessage(requestError) {
  return requestError?.message || 'Task could not be saved'
}

function csrfToken() {
  return document.querySelector('meta[name="csrf-token"]')?.content || ''
}

function clearLockState() {
  lockedInfo.value = null
}

function applyLockError(requestError) {
  const info = lockInfoFromError(requestError)
  if (info.locked_by_label || info.locked_via || info.locked_at) {
    lockedInfo.value = info
  }
}

function lockInfoFromError(requestError) {
  const data = requestError?.data || requestError || {}

  return {
    locked_by_label: data.locked_by_label,
    locked_via: data.locked_via,
    locked_at: data.locked_at,
  }
}

function handleKeydown(event) {
  if (event.key === 'Escape') {
    event.preventDefault()
    requestClose()
    return
  }

  if (event.key !== 'Tab') return

  const focusable = focusableElements()
  if (!focusable.length) {
    event.preventDefault()
    panelRef.value?.focus()
    return
  }

  const first = focusable[0]
  const last = focusable[focusable.length - 1]

  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault()
    last.focus()
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault()
    first.focus()
  }
}

function focusPanel() {
  const target = focusableElements()[0] || panelRef.value
  target?.focus()
}

function focusableElements() {
  if (!panelRef.value) return []

  return [...panelRef.value.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])')]
    .filter((element) => element.getClientRects().length > 0)
}

function lockBodyScroll() {
  if (bodyLocked.value) return

  previousBodyOverflow.value = document.body.style.overflow
  document.body.style.overflow = 'hidden'
  bodyLocked.value = true
}

function restoreBodyScroll() {
  if (!bodyLocked.value) return

  document.body.style.overflow = previousBodyOverflow.value
  bodyLocked.value = false
}

function restoreFocus() {
  if (previousFocus && typeof previousFocus.focus === 'function' && previousFocus.isConnected) {
    previousFocus.focus()
  }
  previousFocus = null
}

function clearCloseAfterErrorTimer() {
  if (!closeAfterErrorTimer.value) return

  window.clearTimeout(closeAfterErrorTimer.value)
  closeAfterErrorTimer.value = null
}

function shouldSkipCleanupForDeletedTask(id) {
  const taskId = normalizeId(id)
  if (!taskId || deletedTaskPendingClose !== taskId) return false

  deletedTaskPendingClose = null
  return true
}

function rememberWorkspaceUrl() {
  if (/^\/workspaces\/[^/]+\/?$/.test(window.location.pathname)) {
    workspaceUrl = currentLocationUrl()
  }
}

function currentLocationUrl() {
  return `${window.location.pathname}${window.location.search}${window.location.hash}`
}

function normalizeId(id) {
  return id === null || id === undefined ? null : String(id)
}
</script>
